import { createContext, useContext, useMemo, useState } from "react"
import { FREE_TIER_ENTITLEMENTS } from "@/consts/freeTierEntitlements.js"

export const PaymentContext = createContext(null)

// DEV-only test helpers: read a localStorage flag safely (private mode can throw).
// Used to force the Free tier / chat-block states without a real tier-0 account.
// `readDevFlag` is opt-in (true only when explicitly '1'); `readDevFlagRaw`
// returns the raw value so a flag can default ON and opt OUT with '0'.
export const readDevFlag = (key) => {
    try {
        return localStorage.getItem(key) === '1'
    } catch {
        return false
    }
}

export const readDevFlagRaw = (key) => {
    try {
        return localStorage.getItem(key)
    } catch {
        return null
    }
}

export const PaymentProvider = ({ children }) => {

    const [plans, setPlans] = useState([])
    const [subscription, setSubscription] = useState(null)
    // Raw entitlements object as computed by the backend (single source of truth
    // for paid tiers). The Free-tier client fences are layered on top below.
    const [rawEntitlements, setRawEntitlements] = useState(null)

    const updateEntitlements = (entitlements) => {
        setRawEntitlements({ ...entitlements })
    }

    // A Free account is tier 0 with no active trial. Paid tiers and trials keep
    // their backend entitlements, so the normal tier logic still overrides the
    // Free defaults (custom-checkout / enterprise accounts carry tier >= 1).
    const isFreeTier = useMemo(() => {
        // DEV-only: the Free tier is forced ON by default in dev so the Free
        // experience is what you see locally. Opt out to test paid/unlocked with:
        //   localStorage.setItem('force_free_tier', '0'); location.reload()
        // (Production is unaffected — this whole branch is stripped by import.meta.env.DEV.)
        if (import.meta.env.DEV && readDevFlagRaw('force_free_tier') !== '0') {
            return true
        }
        if (!rawEntitlements) {
            return false
        }
        // Gate only the Free tier (tier 0). ANY tier > 0 is treated as paid /
        // allowed, so a specific user can be manually unlocked by bumping their
        // tier above 0 (or an active trial).
        return !(rawEntitlements.tier > 0) && !rawEntitlements.activeTrial
    }, [rawEntitlements])

    // The entitlements everyone reads. For Free, apply the client-only fences from
    // FREE_TIER_ENTITLEMENTS over the backend object; for paid/trial, pass through.
    const entitlements = useMemo(() => {
        if (!rawEntitlements || !isFreeTier) {
            return rawEntitlements
        }

        const merged = { ...rawEntitlements }
        Object.entries(FREE_TIER_ENTITLEMENTS).forEach(([key, freeEnt]) => {
            const backendEnt = rawEntitlements[key]
            const next = { ...backendEnt, ...freeEnt }

            // Count caps: keep the live usage the backend already counted, and
            // recompute left / limitReached against the Free limit.
            if (typeof freeEnt.limit === 'number') {
                const used = backendEnt?.used ?? 0
                next.used = used
                next.left = Math.max(0, freeEnt.limit - used)
                next.limitReached = freeEnt.enabled !== false && used >= freeEnt.limit
            }

            merged[key] = next
        })
        return merged
    }, [rawEntitlements, isFreeTier])

    const getEntitlementByKey = (key) => {
        return entitlements?.[key]
    }

    const checkEntitlementAvailable = (key) => {
        // Whole entitlements object hasn't loaded yet (e.g. onboarding renders
        // before the payment fetch). Treat as unavailable rather than crashing.
        if (!entitlements) {
            return { available: false, type: 'notLoaded' }
        }

        const entitlement = entitlements[key]

        // Key not present for this account = not a fence for them → unlimited.
        // (Free-only client keys are absent for paid tiers → paid gets full access.)
        if (!entitlement) {
            return { available: true }
        }

        if (!entitlement.enabled) {
            return { available: false, type: 'notEnabled' }
        }

        if (entitlement.limitReached) {
            return { available: false, type: 'limitReached' }
        }

        return { available: true }
    }

    const checkEntitlementLeft = (key) => {
        const entitlement = getEntitlementByKey(key)
        return entitlement?.left
    }

    return (
        <PaymentContext.Provider
            value={{
                plans,
                setPlans,
                subscription,
                setSubscription,
                getEntitlementByKey,
                updateEntitlements,
                checkEntitlementAvailable,
                checkEntitlementLeft,
                entitlements,
                isFreeTier
            }}
        >
            {children}
        </PaymentContext.Provider>
    )
}

export const usePaymentContext = () => {
    const context = useContext(PaymentContext)

    if (!context) {
        throw new Error('usePaymentContext must be used within a PaymentProvider')
    }

    return context
}
